// The robot-state model: what a robot's per-bucket states *mean*, separate from
// anything that draws them.
//
// These three are needed by DistrictDashboard, OverviewPage and RenewalBrief,
// none of which render the Robots tab, so they live here rather than inside
// `tabs/RobotsTab.js`. A container reaching into a view component for a definition
// is backwards, and it has a concrete cost: RobotsTab pulls in Chart.js and
// react-chartjs-2 at module scope, so asking it what "expired" means would drag a
// charting library into three modules that never plot anything.
//
// The rule worth holding: a tab may import a model, never the reverse.
//
// Deliberately NOT here: UTIL_STATES and STACK_ORDER, the state palette and
// stacking order. Those are design tokens and live in `tokens.js`. Import them
// from there directly -- re-exporting them through a tab or through this module
// is how consumers end up sourcing the palette from a view component instead of
// from where it is defined.

// How long a robot goes without a lesson before it reads as "parked" rather than
// merely idle. A threshold, not a fact about the data: one quiet month is a
// holiday, three is a pattern.
//
// Expressed in MONTHS and converted to buckets per grain, not stored as a bucket
// count. It was 3 buckets, which silently meant three different claims at three
// different grains -- and once the dashboard began defaulting short windows to
// weekly (schoolyear.defaultGrain), a district four weeks into its year had its
// fleet verdict swing from "All clear" to "24 robots to review" purely because
// the chart changed grain. Three quiet weeks is a holiday; the claim this flag
// makes is about a season of silence, so it has to be measured in seasons.
const IDLE_FLAG_MONTHS = 3;

// Buckets of silence that add up to IDLE_FLAG_MONTHS at a given grain. ~4.3 weeks
// to the month, rounded down: a robot quiet for 13 weeks has been quiet for three
// months by any reading, and rounding up would let the last week of a district's
// data decide a redeployment.
const idleFlagBuckets = (interval) =>
  interval === "week" ? 13 : interval === "day" ? 90 : IDLE_FLAG_MONTHS;

// `idle_streak` is a count of trailing in-session buckets with no lessons, so the
// comparison has to know the grain the series was built at. Robots whose payload
// predates the field simply never flag.
const isIdleFlagged = (r, interval) =>
  (r.idle_streak || 0) >= idleFlagBuckets(interval);

// How the flag names itself, at every grain: the wording follows the CLAIM (three
// months of silence), never the bucket count, so the same fleet reads the same way
// whether the reader is looking at a weekly or a monthly strip.
const IDLE_FLAG_LABEL = `${IDLE_FLAG_MONTHS}+ months`;

// A robot whose subscription end date has already passed. A *missing* date is
// unknown, not expired -- we don't hide robots we lack billing data for, because
// "we have no contract on file" and "the contract ended" are different problems
// and only one of them is the district's.
const isExpired = (r) =>
  !!r.sub_end_date && new Date(r.sub_end_date) < new Date();

// The state a robot is in in its LAST BUCKET. Falls back to "dark" for a robot
// with no buckets at all, which is the honest reading -- no evidence of activity
// is what dark means.
//
// This is not "now", and reading it as such is a bug we shipped: the last bucket
// is the current calendar month clipped to today, so on the 1st it is one day
// long and every robot in a working district reads dark. Use `recentState` for
// any "right now" question and keep this for questions genuinely about a bucket.
const lastState = (r) =>
  r.states_by_bucket[r.states_by_bucket.length - 1] || "dark";

// The state a robot is in NOW: active / connected / dark / pre over the trailing
// four weeks, classified server-side over a span that does not move with the day
// of the month (vanroboapp/buckets.py recent_window).
//
// Every "is this robot in use" read on the dashboard belongs here -- the Status
// column, the needs-attention worklist, the fleet rollups, the Overview card.
// The bucket-based reading of the same question changed its answer on the 1st of
// every month, and in the direction that manufactures alarm: a whole district's
// fleet reading dark.
//
// Falls back to the last bucket for a payload that predates the field, so a
// stale cached response degrades to the old behaviour instead of blanking the
// column.
const recentState = (r) => r.recent_state || lastState(r);

export {
  IDLE_FLAG_MONTHS,
  IDLE_FLAG_LABEL,
  idleFlagBuckets,
  isIdleFlagged,
  isExpired,
  lastState,
  recentState,
};
